import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import parse from 'html-react-parser';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Calendar } from 'react-calendar';

import { InputField } from '@components/InputField';
import { handleRenderError, withErrorHandler } from '@utils/errorHandler';

dayjs.extend(customParseFormat);

const MAX_RESPONSE_LENGTH = 200;
const DATE_FORMAT = 'DD/MM/YYYY';
const TODAY = dayjs().format(DATE_FORMAT);

interface IQuestionnaireProperties {
  questions: IQuestion[];
  responses: IQuestionnaireResponse[];
  setBlocked: (blocked: boolean) => void;
  setResponses: Dispatch<SetStateAction<IQuestionnaireResponse[]>>;
}

export const Questionnaire = ({
  questions,
  responses,
  setBlocked,
  setResponses,
}: IQuestionnaireProperties) => {
  const [dateSelectQuestionId, setDateSelectQuestionId] = useState<number>();
  try {
    const checkFormEntriesValid = useCallback(
      withErrorHandler(() => {
        if (questions.length !== responses.length) {
          return setBlocked(true);
        }
        return setBlocked(false);
      }),
      [questions, responses, setBlocked]
    );

    useEffect(() => {
      checkFormEntriesValid();
    }, [checkFormEntriesValid]);

    const handleResponseChange = (questionId: number, value: string) => {
      if (value.length > MAX_RESPONSE_LENGTH) {
        return;
      }
      const responseIndex = responses.findIndex(
        r => r.QuestionId === questionId
      );
      if (value === '') {
        setResponses(r => r.filter(qr => qr.QuestionId !== questionId));
      } else if (responseIndex > -1) {
        setResponses(r => {
          r[responseIndex] = { QuestionId: questionId, Value: value };
          return [...r];
        });
      } else {
        setResponses(r => [...r, { QuestionId: questionId, Value: value }]);
      }
    };

    const toggleResponse = (questionId: number) => {
      if (responses.some(r => r.QuestionId === questionId)) {
        setResponses(r => r.filter(qr => qr.QuestionId !== questionId));
      } else {
        setResponses(r => [...r, { QuestionId: questionId, Value: 'true' }]);
      }
    };

    return (
      <form>
        {questions.map(({ Question, QuestionId, Type }) =>
          Type === 'text' ? (
            <div className='questionWrapper form-row' key={QuestionId}>
              <InputField
                type='text'
                id={Question}
                name={Question}
                label={Question}
                value={
                  responses.find(r => r.QuestionId === QuestionId)?.Value ?? ''
                }
                maxLength={MAX_RESPONSE_LENGTH}
                onChange={withErrorHandler(uiEvent => {
                  const target = uiEvent.target as HTMLTextAreaElement;
                  handleResponseChange(QuestionId, target.value);
                })}
                required
              />
            </div>
          ) : Type === 'date' ? (
            <div
              className='questionWrapper form-row'
              key={QuestionId}
              onClick={() => setDateSelectQuestionId(QuestionId)}
            >
              <InputField
                type='text'
                id={Question}
                name={Question}
                label={Question}
                placeholder={DATE_FORMAT}
                autoComplete='off'
                value={
                  responses.find(r => r.QuestionId === QuestionId)?.Value ?? ''
                }
                maxLength={MAX_RESPONSE_LENGTH}
                onChange={withErrorHandler(() => {
                  setDateSelectQuestionId(QuestionId);
                })}
                required
              />
              {dateSelectQuestionId === QuestionId && (
                <div className='date-picker-wrapper'>
                  <Calendar
                    onChange={selectedDate => {
                      handleResponseChange(
                        QuestionId,
                        dayjs(selectedDate as Date).format(DATE_FORMAT)
                      );
                      // NOTE: delayed to override the click handler on the wrapper
                      setTimeout(() => setDateSelectQuestionId(undefined), 50);
                    }}
                    value={dayjs(
                      responses.find(r => r.QuestionId === QuestionId)?.Value ??
                        TODAY,
                      DATE_FORMAT
                    ).toDate()}
                    prev2Label={null}
                    next2Label={null}
                    minDetail='month'
                    calendarType='iso8601'
                  />
                </div>
              )}
            </div>
          ) : (
            // checkbox
            <div
              className='questionWrapper'
              key={QuestionId}
              onClick={withErrorHandler(() => toggleResponse(QuestionId))}
            >
              <div className='radioRow'>
                <svg
                  className={`bulletCheck ${
                    responses.some(r => r.QuestionId === QuestionId)
                      ? 'checked'
                      : ''
                  }`}
                  data-name='bulletCheck'
                  xmlns='http://www.w3.org/2000/svg'
                  viewBox='0 0 22 22'
                >
                  <path
                    className='bullet'
                    d='M11,22A11,11,0,1,1,22,11,10.96832,10.96832,0,0,1,11,22ZM11,2a9,9,0,1,0,9,9A8.96258,8.96258,0,0,0,11,2Z'
                  />
                  <circle className='bulletChecked' cx='11' cy='11' r='5' />
                </svg>
                <label className='radioLabel' htmlFor='yes'>
                  {parse(Question)}
                </label>
              </div>
            </div>
          )
        )}
        <input type='submit' hidden />
      </form>
    );
  } catch (error) {
    return handleRenderError(error);
  }
};
